import { DateTime } from "luxon";
import { type MouseEvent, type SyntheticEvent, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RecurringShiftDisplayMode } from "../../../components/RecurringShift/util/useRecurringShiftState";
import type { ExactShiftFilterType } from "../../../components/staffing/MonthlyView/utils";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useGlobalManualLocationOrder } from "../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { consecutiveMasterScheduleSortingKey, localStorageService } from "../../../services/LocalStorageService";
import type { DepartmentLocation, Nurse, NurseRole, RecurringShift, RecurringShiftRecurrenceType } from "../../../utils/models";
import { compareValues, type SortingState } from "../../../utils/multiColumnSorting";
import { longPtoMinimumDays } from "../LongPTOButton/LongPTOButton";
import { usePersistedExclusionFilter, usePersistedShiftFilter } from "../usePersistedScheduleTemplateFilter";
import { CellMenuAction } from "../v2/auxillary/RecurringMasterScheduleCellMenu";
import { useFetchMasterSchedule } from "../v2/hooks";
import { masterScheduleDefaultSorting, type Section, type SectionLocationMapReadOnly, toggleMasterScheduleNurseCellHighlight } from "../v2/utils";
import { buildConsecutiveScheduleData } from "./buildConsecutiveScheduleData";
import { getCycleStartDate } from "./consecutiveDateMath";
import { CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID, type ConsecutivePeriod, DEFAULT_CONSECUTIVE_PERIOD, getCycleLength } from "./consts";

/** Consecutive shifts are the only recurrence type this view shows (bi-weekly/3-weekly are Weekly+interval). */
const CONSECUTIVE_RECURRING_TYPE = "ConsecutiveDays" as const satisfies keyof typeof RecurringShiftRecurrenceType;

type ClickedCellWithAnchor = {
	anchorEl: HTMLElement;
	recurringShift: RecurringShift;
	clickedDate: DateTime;
	clickedLocation: DepartmentLocation;
};

type EditOrCreateRecurringShiftState = {
	mode: RecurringShiftDisplayMode;
	clickedDate: DateTime;
	recurringShift?: RecurringShift;
	clickedLocation: DepartmentLocation;
};

export const useConsecutiveMasterScheduleState = () => {
	const { trackError } = useAnalytics();
	const navigate = useNavigate();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const {
		roster,
		getNurseById,
		getNurseFormattedName,
		isLoading: isLoadingRoster,
		isError: isRosterError,
		refetch: refetchRoster,
	} = useRosterLookup(chosenDepartment.department_id);
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const enabledJobs = useJobs();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const { expandedGroups, setExpandedGroups, toggleGroupExpand } = useExpandedGroups();
	const lastInitializedDepartmentRef = useRef<string | null>(null);

	// Persisted sort (Location Name / Shift Times), scoped to the consecutive view's own storage key.
	const [masterScheduleSortState, setMasterScheduleSortState] = useState<SortingState<DepartmentLocation>[]>(
		() => localStorageService.getTableSortingState<DepartmentLocation>(consecutiveMasterScheduleSortingKey, userId) ?? masterScheduleDefaultSorting,
	);

	// Persisted consecutive rotation (N-on / M-off). Drives the grid's cycle length and which shifts show.
	const [period, setPeriodState] = useState<ConsecutivePeriod>(
		() => (userId ? localStorageService.getConsecutivePeriod(userId) : null) ?? DEFAULT_CONSECUTIVE_PERIOD,
	);
	const setPeriod = useCallback(
		(next: ConsecutivePeriod) => {
			setPeriodState(next);
			if (userId) localStorageService.setConsecutivePeriod(next, userId);
		},
		[userId],
	);
	const cycleLength = getCycleLength(period);

	const today = useMemo(() => DateTime.now().setZone(timezone).startOf("day"), [timezone]);
	const cycleStartDate = useMemo(
		() => getCycleStartDate({ today, firstSunday: chosenDepartment.first_sunday, cycleLength, timezone }),
		[today, chosenDepartment.first_sunday, cycleLength, timezone],
	);

	const filtersBoxRef = useRef<HTMLDivElement>(null);

	const {
		data: recurringShiftsData,
		isLoading: isLoadingRecurringShifts,
		isError: isRecurringShiftsError,
		refetch: refetchRecurringShifts,
	} = useFetchMasterSchedule({
		startDate: cycleStartDate,
		recurringTypes: [CONSECUTIVE_RECURRING_TYPE],
	});

	const consecutiveShifts = useMemo(
		() => recurringShiftsData?.filter((shift) => !shift.deleted && shift.recurringParams.recurringType === "ConsecutiveDays"),
		[recurringShiftsData],
	);

	const periodMatchingShifts = useMemo(
		() =>
			// Only consecutive shifts whose rotation matches the selected period are shown (the grid draws a
			// single cycle length, so mixing 4/2 with 5/2 etc. would be inconsistent).
			consecutiveShifts?.filter(
				(shift) =>
					shift.recurringParams.recurringType === "ConsecutiveDays" &&
					shift.recurringParams.working_days_amount === period.workingDays &&
					shift.recurringParams.rest_days_amount === period.restDays,
			),
		[consecutiveShifts, period.workingDays, period.restDays],
	);

	// ---- Filters ----
	const [selectedRoleFilters, setSelectedRoleFilters] = usePersistedExclusionFilter("consecutive", "roles", chosenDepartment.roles);
	const onSelectedRoleFiltersChanged = useCallback(
		(selected: (NurseRole | "all")[]) =>
			setSelectedRoleFilters(selected.includes("all") ? chosenDepartment.roles : selected.filter((role): role is NurseRole => role !== "all")),
		[chosenDepartment.roles, setSelectedRoleFilters],
	);
	const deferredSelectedRoleFilters = useDeferredValue(selectedRoleFilters);

	const allJobIds = useMemo(() => enabledJobs.map((j) => j.id), [enabledJobs]);
	const [selectedJobFilters, onSelectedJobFiltersChanged] = usePersistedExclusionFilter("consecutive", "jobs", allJobIds);
	const deferredSelectedJobFilters = useDeferredValue(selectedJobFilters);

	const allShiftsTimes = useMemo<ExactShiftFilterType[]>(
		() =>
			periodMatchingShifts?.map((value) => ({
				start: DateTime.fromFormat(value.shiftStart.substring(0, 5), "HH:mm"),
				end: DateTime.fromFormat(value.shiftEnd.substring(0, 5), "HH:mm"),
			})) ?? // DEFAULT: shifts still loading — no time chips on offer yet, only All.
			[],
		[periodMatchingShifts],
	);
	const { selectedShiftFilter, handleShiftFilterChange, offeredShiftWindows } = usePersistedShiftFilter("consecutive", allShiftsTimes);
	const deferredSelectedShiftFilter = useDeferredValue(selectedShiftFilter);

	const availableLocations = useMemo(() => {
		const names = Array.from(
			new Set([...chosenDepartment.locations.map((loc) => loc.location ?? ""), ...(periodMatchingShifts?.map((shift) => shift.location ?? "") ?? [])]),
		);
		return names.sort((a, b) => compareValues(a, b, customStringOrderMap));
	}, [chosenDepartment.locations, periodMatchingShifts, customStringOrderMap]);
	const [selectedLocationFilters, onSelectedLocationFiltersChanged] = usePersistedExclusionFilter("consecutive", "locations", availableLocations);
	const deferredSelectedLocationFilters = useDeferredValue(selectedLocationFilters);

	const [selectedNurseToFilterBy, setSelectedNurseToFilterBy] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const handleNurseFilterChange = useCallback((_: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => setSelectedNurseToFilterBy(value), []);
	const deferredSelectedNurseToFilterBy = useDeferredValue(selectedNurseToFilterBy);

	// ---- Grid derivation (pure) ----
	const built = useMemo(
		() =>
			recurringShiftsData === undefined || roster === undefined
				? null
				: buildConsecutiveScheduleData({
						shifts: periodMatchingShifts,
						chosenDepartment,
						cycleStartDate,
						daysPerBlock: cycleLength,
						timezone,
						today,
						jobManagementEnabled,
						selectedRoleFilters: deferredSelectedRoleFilters,
						selectedJobFilters: deferredSelectedJobFilters,
						selectedLocationFilters: deferredSelectedLocationFilters,
						selectedShiftFilter: deferredSelectedShiftFilter,
						nurseFilterId: deferredSelectedNurseToFilterBy?.value.id ?? null,
						masterScheduleSortState,
						sectionOrder: [],
						customStringOrderMap,
						getNurseById,
						getNurseFormattedName,
						longPtoMinimumDays,
					}),
		[
			recurringShiftsData,
			roster,
			periodMatchingShifts,
			chosenDepartment,
			cycleStartDate,
			cycleLength,
			timezone,
			today,
			jobManagementEnabled,
			deferredSelectedRoleFilters,
			deferredSelectedJobFilters,
			deferredSelectedLocationFilters,
			deferredSelectedShiftFilter,
			deferredSelectedNurseToFilterBy,
			masterScheduleSortState,
			customStringOrderMap,
			getNurseById,
			getNurseFormattedName,
		],
	);
	const calculatedData: SectionLocationMapReadOnly | null = built?.data ?? null;
	const isCalculating = isLoadingRecurringShifts || isLoadingRoster;
	const loadError = isRecurringShiftsError || isRosterError;
	// The grid's error state offers one retry for whichever of its two loads failed.
	const retryLoad = useCallback(() => {
		if (isRosterError) refetchRoster();
		if (isRecurringShiftsError) void refetchRecurringShifts();
	}, [isRosterError, refetchRoster, isRecurringShiftsError, refetchRecurringShifts]);

	// A filter differing from its deferred counterpart means the visible grid is stale (recompute pending).
	const isStale =
		!isCalculating &&
		(selectedRoleFilters !== deferredSelectedRoleFilters ||
			selectedJobFilters !== deferredSelectedJobFilters ||
			selectedLocationFilters !== deferredSelectedLocationFilters ||
			selectedShiftFilter !== deferredSelectedShiftFilter ||
			selectedNurseToFilterBy !== deferredSelectedNurseToFilterBy);

	useEffect(() => {
		if (!built) return;
		for (const placementError of built.placementErrors) {
			trackError("ConsecutiveMasterSchedule_shiftPlacement_locationSectionNotFound", placementError);
		}
	}, [built, trackError]);

	// Expand all sections on first load / department switch.
	useEffect(() => {
		if (!calculatedData) return;
		if (lastInitializedDepartmentRef.current === chosenDepartment.department_id) return;
		lastInitializedDepartmentRef.current = chosenDepartment.department_id;
		setExpandedGroups(new Set(Object.keys(calculatedData) as Section[]));
	}, [calculatedData, chosenDepartment.department_id, setExpandedGroups]);

	// Highlight the filtered nurse's cells. Driven by what is actually rendered (`calculatedData`,
	// `expandedGroups`) and applied on the next frame after commit (mirrors MasterScheduleV2).
	useEffect(() => {
		if (!selectedNurseToFilterBy) return;
		const nurseId = selectedNurseToFilterBy.value.id;
		const rafId = requestAnimationFrame(() => toggleMasterScheduleNurseCellHighlight(nurseId, true, CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID));
		return () => {
			cancelAnimationFrame(rafId);
			toggleMasterScheduleNurseCellHighlight(nurseId, false, CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID);
		};
	}, [selectedNurseToFilterBy, calculatedData, expandedGroups]);

	// ---- Cell menu + add/edit popup ----
	const [currentlyClickedCell, setCurrentlyClickedCell] = useState<ClickedCellWithAnchor | null>(null);
	const [editOrCreateRecurringShiftState, setEditOrCreateRecurringShiftState] = useState<EditOrCreateRecurringShiftState | null>(null);

	const onShiftClick = useCallback(
		(recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) =>
			setCurrentlyClickedCell({ anchorEl: event.currentTarget as HTMLElement, recurringShift, clickedDate, clickedLocation }),
		[],
	);
	const onShiftKeyDown = useCallback(
		(recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) =>
			(event.key === "Enter" || event.key === " ") &&
			setCurrentlyClickedCell({ anchorEl: event.currentTarget as HTMLElement, recurringShift, clickedDate, clickedLocation }),
		[],
	);

	const onShiftMenuAction = useCallback(
		(action: CellMenuAction) => {
			if (action === CellMenuAction.Cancel) {
				setCurrentlyClickedCell(null);
			} else if (action === CellMenuAction.GoToProfile) {
				if (currentlyClickedCell?.recurringShift) {
					navigate(`/${chosenDepartment.department_id}/nurse/${currentlyClickedCell.recurringShift.nurseId}`, {
						state: { navigationSrc: window.location.pathname },
					});
					setCurrentlyClickedCell(null);
				}
			} else if (action === CellMenuAction.Edit) {
				if (currentlyClickedCell?.recurringShift) {
					setEditOrCreateRecurringShiftState({
						mode: RecurringShiftDisplayMode.Edit,
						clickedDate: currentlyClickedCell.clickedDate,
						recurringShift: currentlyClickedCell.recurringShift,
						clickedLocation: currentlyClickedCell.clickedLocation,
					});
					setCurrentlyClickedCell(null);
				}
			}
		},
		[currentlyClickedCell, navigate, chosenDepartment.department_id],
	);

	const handleOnAddClick = useCallback(
		(clickedDate: DateTime, clickedLocation: DepartmentLocation) =>
			setEditOrCreateRecurringShiftState({ mode: RecurringShiftDisplayMode.Create, clickedDate, clickedLocation }),
		[],
	);

	const handleRecurringShiftComponentOnClose = useCallback(
		async (didUpdateOrCreate?: boolean) => {
			setEditOrCreateRecurringShiftState(null);
			if (didUpdateOrCreate) await refetchRecurringShifts();
		},
		[refetchRecurringShifts],
	);

	const handleSectionCollapseToggle = useCallback((section: Section) => toggleGroupExpand(section), [toggleGroupExpand]);

	return {
		// data
		calculatedData,
		isCalculating,
		loadError,
		retryLoad,
		isStale,
		cycleStartDate,
		today,
		timezone,
		period,
		setPeriod,
		cycleLength,
		chosenDepartment,
		refetchRecurringShifts,
		getNurseById,
		expandedGroups,
		handleSectionCollapseToggle,
		// refs
		filtersBoxRef,
		// menu + popup
		currentlyClickedCell,
		editOrCreateRecurringShiftState,
		onShiftClick,
		onShiftKeyDown,
		onShiftMenuAction,
		handleOnAddClick,
		handleRecurringShiftComponentOnClose,
		// filters
		selectedShiftFilter,
		handleShiftFilterChange,
		allShiftsTimes,
		offeredShiftWindows,
		masterScheduleSortState,
		setMasterScheduleSortState,
		selectedRoleFilters,
		onSelectedRoleFiltersChanged,
		departmentSpecificRoles: chosenDepartment.roles,
		departmentJobs: enabledJobs,
		selectedJobFilters,
		onSelectedJobFiltersChanged,
		isJobFilterActive: selectedJobFilters.length < allJobIds.length,
		jobManagementEnabled,
		availableLocations,
		selectedLocationFilters,
		onSelectedLocationFiltersChanged,
		handleNurseFilterChange,
		selectedNurseToFilterBy,
		periodMatchingShifts,
	};
};
